'use client';

import { useLocale, useTranslations } from 'next-intl';
import { ComponentProps, ComponentPropsWithoutRef, ReactNode, useContext } from 'react';
import { getFormattedVenueOpeningHours } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import { useVenueDistance } from '@/entities/venue/lib/useVenueDistance';
import { getVenueAvailabilityStatus } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { VenueStatus as VenueStatusComponent } from '@/entities/venue/ui/venue-status';
import { VenueContext } from '@/features/venue-router';
import { cn } from '@/shared/lib/cn';
import { formatAddress } from '@/shared/lib/formatAddress';
import { getFormatedPhoneNumber } from '@/shared/lib/getFormatedPhoneNumber';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Tags } from '@/shared/ui/tags';
import { TagsT } from '@/shared/ui/tags/types';
import { VenueCardInfo } from './venue-card-info';
import './venue-card.scss';

import { useGetVenueHrefAttributes } from '@/features/venue-router';

const useVenue = () => {
    const ctx = useContext(VenueContext);
    return ctx?.data ?? null;
};

type WrapperProps = ComponentPropsWithoutRef<typeof Card> & { children?: ReactNode };

export const VenueWrapper = ({ children, className, ...props }: WrapperProps) => {
    const venueCtx = useContext(VenueContext);
    const venue = venueCtx?.data ?? null;
    const address = venueCtx?.address ?? null;
    const deliveryType = venueCtx?.deliveryType ?? null;

    const getVenueHrefAttributes = useGetVenueHrefAttributes();

    if (!venue) {
        return null;
    }

    const { href, onClick, onKeyDown } = getVenueHrefAttributes(venue.apiPartnerSettingsId, {
        address,
        deliveryType,
    });

    return (
        <Card
            className={cn('venue-card', className)}
            data-testid="venue-card"
            data-id={venue.id}
            shadow
            {...props}
        >
            <a href={href} onClick={onClick} onKeyDown={onKeyDown} className="venue-card__link">
                {children}
            </a>
        </Card>
    );
};

export const VenueImage = ({ className, ...props }: ComponentProps<typeof Layout>) => {
    const venue = useVenue();
    const overrideUrl = venue?.previewUrl;
    const imageAsset = venue && !overrideUrl ? getMainAsset(venue.assets) : null;
    const url = overrideUrl ?? imageAsset?.fullUrl;
    const objectFit = venue?.previewObjectFit;
    if (!venue || !url) return null;

    return (
        <Layout className={cn('venue-card__image', className)} {...props}>
            <Media
                data={{ url, alt: 'Thumbnail' }}
                alt={`venue-${venue.name}-image`}
                sizes="19.5rem"
                data-testid="venue-card-image"
                style={objectFit ? { objectFit } : undefined}
            />
        </Layout>
    );
};

type TagsProps = ComponentProps<typeof Tags> & { tags?: TagsT };
export const VenueTags = ({ tags, ...props }: TagsProps) => {
    const venue = useVenue();
    if (!venue) return null;
    return <Tags tags={tags} {...props} />;
};

export const VenueLogo = ({ className, media, ...props }: ComponentProps<typeof Layout>) => {
    const venue = useVenue();
    const overrideUrl = venue?.logoUrl;
    const logoAsset = venue && !overrideUrl ? venue.assets.find((a) => a.flags?.includes('LOGO')) : null;
    const url = overrideUrl ?? logoAsset?.fullUrl;
    if (!venue || !url) return null;

    return (
        <Layout
            className={cn('venue-card__logo', className)}
            media={{
                ...media,
                initial: {
                    flexShrink: 0,
                    ...media?.initial,
                },
            }}
            {...props}
        >
            <Media
                data={{ url, alt: 'Logo' }}
                alt={`venue-${venue.name}-logo`}
                sizes="4rem"
                data-testid="venue-card-logo"
            />
        </Layout>
    );
};

export const VenueTitle = (props: ComponentProps<typeof Layout>) => {
    const venue = useVenue();
    if (!venue) return null;
    return (
        <Layout element="span" data-testid="venue-card-title" {...props}>
            {venue.name}
        </Layout>
    );
};

export const VenueStatus = () => {
    const t = useTranslations();
    const locale = useLocale();
    const venue = useVenue();
    const storeStatus = useVenueAvailabilityStore((s) => s.status);
    const storeOpeningHours = useVenueAvailabilityStore((s) => s.formattedOpeningHours);
    if (!venue) return null;

    if (venue.isCurrent) {
        return (
            <VenueStatusComponent
                t={t}
                locale={locale}
                openingHours={storeOpeningHours}
                status={storeStatus}
            />
        );
    }

    const formattedOpeningHours = getFormattedVenueOpeningHours(venue.openingHours ?? [], t);
    const status = getVenueAvailabilityStatus({
        openingHours: formattedOpeningHours,
        // TODO: outages are not loaded for non-current venues yet — card status ignores foreign-venue outages
        outages: [],
    });
    return (
        <VenueStatusComponent t={t} locale={locale} openingHours={formattedOpeningHours} status={status} />
    );
};

export const VenueAddress = (props: ComponentPropsWithoutRef<'div'>) => {
    const venue = useVenue();
    const venueDistance = useVenueDistance();
    if (!venue?.address) return null;
    return (
        <VenueCardInfo
            data-testid="venue-card-address"
            icon={(svgProps) => <Icon name="scan" {...svgProps} />}
            {...props}
        >
            {`${venueDistance ? `${venueDistance}, ` : ''}${formatAddress(venue.address)}`}
        </VenueCardInfo>
    );
};

export const VenueTelephone = (props: ComponentPropsWithoutRef<'div'>) => {
    const venue = useVenue();

    if (!venue?.contactInfo?.telephonePrefix || !venue?.contactInfo?.telephone) {
        return null;
    }
    const telephone = getFormatedPhoneNumber(
        venue?.contactInfo?.telephonePrefix,
        venue?.contactInfo?.telephone,
    );
    return (
        <VenueCardInfo
            data-testid="venue-card-telephone"
            icon={(svgProps) => <Icon name="phone" {...svgProps} />}
            {...props}
        >
            {telephone.formated}
        </VenueCardInfo>
    );
};

export const VenueEmail = (props: ComponentPropsWithoutRef<'div'>) => {
    const venue = useVenue();
    const email = venue?.contactInfo?.email;
    if (!email) return null;
    return (
        <VenueCardInfo
            data-testid="venue-card-email"
            icon={(svgProps) => <Icon name="send" {...svgProps} />}
            href={`mailto:${email}`}
            {...props}
        >
            {email}
        </VenueCardInfo>
    );
};

export const VenueChevron = (props: ComponentPropsWithoutRef<'span'>) => {
    const venue = useVenue();
    if (!venue) return null;
    return (
        <span className="venue-card__chevron" data-testid="venue-card-chevron" {...props}>
            <Icon name="chevron-right" />
        </span>
    );
};

export const VenueDistance = (props: ComponentPropsWithoutRef<'span'>) => {
    const distance = useVenueDistance();
    if (!distance) return null;
    return (
        <span className="venue-card__distance" data-testid="venue-card-distance" {...props}>
            {distance}
        </span>
    );
};

export {
    VenueWrapper as Wrapper,
    VenueImage as Image,
    VenueTags as Tags,
    VenueLogo as Logo,
    VenueTitle as Title,
    VenueStatus as Status,
    VenueAddress as Address,
    VenueTelephone as Telephone,
    VenueEmail as Email,
    VenueChevron as Chevron,
    VenueDistance as Distance,
};
